// Landing-page SEO share-image uploader. Three-step flow per file:
//   1. POST /landing-pages/{id}/seo/image/sign   → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /landing-pages/{id}/seo/image/commit → returns { seo_image_url }
// "Remove" hits DELETE /landing-pages/{id}/seo/image. The widget is
// re-wired after every htmx swap, so the SEO popover gets enhanced when
// it loads.

import { onceGuard } from "./bind_once.js";
import { postForm, request, signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-seo-image]";
const FALLBACK_UPLOAD_ERROR = "Could not upload your image. Please try again.";
const FALLBACK_REMOVE_ERROR = "Could not remove your image. Please try again.";

export function initSEOImage(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const pageID = widget.dataset.seoImagePageId;
  const input = widget.querySelector("[data-seo-image-input]");
  const removeBtn = widget.querySelector("[data-seo-image-remove]");
  const preview = widget.querySelector("[data-seo-image-preview]");
  const placeholder = widget.querySelector("[data-seo-image-placeholder]");
  const errorEl = widget.querySelector("[data-seo-image-error]");
  const statusEl = widget.querySelector("[data-seo-image-status]");
  if (!pageID || !input) return;

  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) return;

    hide(errorEl);
    setStatus(statusEl, "Uploading…");
    input.disabled = true;

    try {
      const url = await uploadOne(pageID, file);
      showPreview(preview, placeholder, removeBtn, url);
    } catch (err) {
      console.error("seo image upload failed", err);
      showError(errorEl, err.userMessage || FALLBACK_UPLOAD_ERROR);
    } finally {
      hide(statusEl);
      input.disabled = false;
      input.value = "";
    }
  });

  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      hide(errorEl);
      removeBtn.disabled = true;
      setStatus(statusEl, "Removing…");

      try {
        await request(`/landing-pages/${pageID}/seo/image`, {
          method: "DELETE",
        });
        clearPreview(preview, placeholder, removeBtn);
      } catch (err) {
        console.error("seo image removal failed", err);
        showError(errorEl, err.userMessage || FALLBACK_REMOVE_ERROR);
      } finally {
        hide(statusEl);
        removeBtn.disabled = false;
      }
    });
  }
}

async function uploadOne(pageID, file) {
  const base = `/landing-pages/${pageID}/seo/image`;

  const signed = await signAndPut(`${base}/sign`, file);

  const committed = await postForm(`${base}/commit`, { key: signed.key });

  return committed.seo_image_url || "";
}

function showPreview(preview, placeholder, removeBtn, url) {
  if (preview && url) {
    // Fade the freshly-uploaded image in once it loads, so it doesn't
    // pop in over the placeholder.
    preview.style.opacity = "0";
    preview.addEventListener(
      "load",
      () => {
        preview.style.opacity = "1";
      },
      { once: true },
    );
    preview.classList.remove("hidden");
    preview.src = url;
  }
  if (placeholder) placeholder.classList.add("hidden");
  if (removeBtn) removeBtn.classList.remove("hidden");
}

function clearPreview(preview, placeholder, removeBtn) {
  if (preview) {
    preview.removeAttribute("src");
    preview.classList.add("hidden");
  }
  if (placeholder) placeholder.classList.remove("hidden");
  if (removeBtn) removeBtn.classList.add("hidden");
}

function setStatus(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hide(el) {
  if (el) el.classList.add("hidden");
}

function showError(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}
